import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import styled from "styled-components";
import { v4 as uuidv4 } from "uuid";
import * as Library from "../../../utils/Library";
import ProductGridCard from "../gridCards/ProductGridCard";
import CardGridSkeleton from "../gridCards/animations/CardGridSkeleton";
import { formatDefaultDateTime } from "../../../utils/Formatter";
import { AppQueryClient } from "../../../api/queryClient";

// #region  SCOPED CSS
const StyledPage = styled.div`
    & {
        overflow-y: scroll;
        height: 100%;
        margin-right: -24px;
        margin-left: -24px;
        padding-left: 24px;
        padding-right: 17px;
    }
`;
// #endregion

/**
 * Renders an infinite grid of products cards
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The additional CSS class name for the component.
 * @param {number} [props.limit=10] - The maximum number of cards to load per page.
 * @param {string} [props.queryKey] - The query key for fetching data.
 * @param {number} [props.minimumCardsFill] - The minimum number of cards to fill the grid.
 * @param {number} [props.minColumnWidth] - The minimum width of each grid column.
 * @param {Function} [props.onEditAction] - The callback function for the edit action.
 * @param {Function} [props.onDeleteAction] - The callback function for the delete action.
 * @param {Function} [props.onSuspendAction] - The callback function for the suspend action.
 * @param {string} [props.statusFromPath] - The status from the path.
 * @param {Object} [props.updateDates] - The update dates object.
 * @param {Function} [props.setUpdateDates] - The callback function for the update dates.
 * @returns {JSX.Element} The rendered component.
 */
export default forwardRef(function InfinityGridProductsCards(props, ref) {
    // #region VARS
    const pageLimit = props?.limit || 10;

    const gridLayoutStyle = {
        display: "grid",
        gridTemplateColumns: `repeat(auto-fill, minmax(${props?.minColumnWidth || "395px"}, 1fr))`,
        gap: "24px"
    };

    const scrollContainerRef = useRef(null);
    const [scroll, setScroll] = useState(true);

    const [minimumCardsFill, setminimumCardsFill] = useState(1); //props?.minimumCardsFill || pageLimit
    const requestEnabled = props?.requestEnabled ?? true;

    const [queryKey, setQueryKey] = useState(props?.queryKey ?? uuidv4());
    // #endregion

    // #region COMPUTED PROPERTIES
    const filters = props?.filters;

    const {
        data,
        isFetching: isLoading,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage
    } = Library.usePostInfiniteQuery(
        queryKey,
        "getProductsData",
        {
            companyID: localStorage.getItem("currentCompanyID"),
            limit: pageLimit,
            searchName: props?.searchName,
            searchText: props?.barcode,
            status: props?.status,
            filters
        },
        () => {
            setScroll(true);
        },
        true,
        "Products",
        false,
        requestEnabled
    );

    const pageList = data?.pages;
    const nextDataCount = data?.pages[data?.pages.length - 1].data?.nextDataCount;
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        if (props?.queryKey) setQueryKey(props?.queryKey);
    }, [props?.queryKey]);

    useEffect(() => {
        handleScroll();

        setminimumCardsFill(getTotalCards() + 1);

        if (data?.pages?.length > 0) {
            const lastPage = data?.pages?.[data?.pages?.length - 1]?.data;

            if (!lastPage?.lastUpdateDate || !lastPage?.dateAsOf) {
                props.setUpdateDates({ show: false });
                return;
            }

            props.setUpdateDates({
                show: true,
                lastUpdatedDateTime: formatDefaultDateTime(lastPage?.lastUpdateDate, "--"),
                dataAsOfDateTime: formatDefaultDateTime(lastPage?.dateAsOf, "")
            });
        } else props.setUpdateDates({ show: false });
    }, [data?.pages]);

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer && !isLoading && scroll && !isFetchingNextPage) {
            scrollContainer.addEventListener("scroll", handleScroll);
        }

        return () => {
            scrollContainer?.removeEventListener("scroll", handleScroll);
        };
    }, [scrollContainerRef, isLoading, pageList, scroll, isFetchingNextPage, data?.pages, hasNextPage]);

    useEffect(() => {
        props.setData(data); // Update the data state in the parent component
    }, [data]);
    // #endregion

    // #region METHODS
    const handleScroll = () => {
        const scrollContainer = scrollContainerRef.current;

        const scrolledRatio = scrollContainer.scrollTop / (scrollContainer.scrollHeight - scrollContainer.clientHeight);
        const isAtBottom = scrolledRatio >= 0.6;

        if (isAtBottom && !isLoading && hasNextPage && !isFetchingNextPage) {
            setScroll(false);
            fetchNextPage();
        }
    };

    const getTotalCards = () => {
        return pageList?.reduce((acc, it) => {
            return acc + it?.data?.result?.length;
        }, 0);
    };

    const handleCheckboxClick = (id, isSelected) => {
        if (isSelected) {
            props.setSelectedProducts([...props.selectedProducts, id]);
        } else {
            props.setSelectedProducts(props.selectedProducts.filter(productId => productId !== id));
        }
    };

    const handleReset = infiniteGridQuerykey => {
        scrollContainerRef.current.scrollTop = 0;
        setScroll(true);
        AppQueryClient.clear();

        const newQueryKeyTMP = [...infiniteGridQuerykey];
        newQueryKeyTMP["refresh"] = uuidv4();
        setQueryKey(newQueryKeyTMP);
    };
    //#endregion

    useImperativeHandle(ref, () => ({
        reset: handleReset
    }));

    // #region RENDER
    return (
        <StyledPage className={`library-scrollable ${props?.className || ""}`} ref={scrollContainerRef}>
            {isLoading && !isFetchingNextPage ? (
                <div style={gridLayoutStyle}>
                    {Array.from({ length: pageLimit }, () => (
                        <CardGridSkeleton key={uuidv4()} height="818.28px" imageHeight="465px" />
                    ))}
                </div>
            ) : (
                <div className="mb-24px" style={gridLayoutStyle}>
                    {pageList?.map(it => {
                        return it?.data?.result?.map(item => {
                            return (
                                <ProductGridCard
                                    key={item?.botagID}
                                    title={item?.styleName}
                                    labelBrandName={item?.labelBrandName}
                                    image={item?.image}
                                    //images={item?.images}
                                    //images={item?.colors?.[0]?.images}
                                    isShowCarousel={false}
                                    id={item?.botagID}
                                    productID={item?.productID}
                                    status={item?.status}
                                    createdAt={item?.createdAt}
                                    collectionName={item?.collectionName}
                                    lastStockCheck={item?.lastStockCheck}
                                    supplierStock={item?.supplierStock}
                                    totalStockInStores={item?.totalStockInStores}
                                    totalStoresWithStock={item?.totalStoresWithStock}
                                    isCardEnabled={item?.status?.toLowerCase() !== "suspended"}
                                    editIcon={{ onClick: props?.onClickEdit && (() => props?.onClickEdit(item)) }}
                                    removeIcon={{ onClick: props?.onClickRemove && (() => props?.onClickRemove(item)) }}
                                    suspendIcon={{
                                        onClick: props?.onClickSuspend && (() => props?.onClickSuspend(item))
                                    }}
                                    hideActionBtn={props?.hideActionBtn ? props?.hideActionBtn : false}
                                    primaryButton={{
                                        title: props?.primaryButton?.title ? props?.primaryButton?.title : "DETAILS",
                                        onClick: props?.onClickDetails ? () => props?.onClickDetails(item) : undefined
                                    }}
                                    secondaryButton={{
                                        title: props?.secondaryButton?.title
                                            ? props?.secondaryButton?.title
                                            : "METRICS",
                                        onClick: props?.onClickMetrics ? () => props?.onClickMetrics(item) : undefined
                                    }}
                                    data={{ ...item }}
                                    //colorNames={item?.colorNames}
                                    colors={item?.colors}
                                    colorNames={item?.colors?.map(it => it?.name)}
                                    isSelected={props.selectedProducts?.includes(item?.productID) || false}
                                    onCheckboxClick={handleCheckboxClick}
                                    hideSelections={props?.hideSelections ? props?.hideSelections : false}
                                    boTagData={item}
                                    highlighted={item?.status === "New" || item?.status === "Suspended" ? true : false}
                                    highlightColor={
                                        item?.status === "New" ? "#0b0" : item?.status === "Suspended" ? "#f90" : ""
                                    }
                                    highlightCustomText={item?.status}
                                />
                            );
                        });
                    })}

                    {!isFetchingNextPage &&
                        minimumCardsFill - getTotalCards() > 0 &&
                        Array.from({ length: minimumCardsFill - getTotalCards() }, (_, index) => (
                            <ProductGridCard
                                key={index}
                                isCardEnabled={false}
                                hideActionBtn={props?.hideActionBtn ? props?.hideActionBtn : false}
                                hideSelections={true}
                            />
                        ))}

                    {isFetchingNextPage &&
                        Array.from({ length: nextDataCount }, (_, index) => (
                            <CardGridSkeleton key={index} values={[]} height="818.28px" imageHeight="465px" />
                        ))}
                </div>
            )}
        </StyledPage>
    );
    // #endregion
});
